import React, { createContext, ReactNode, useState, useMemo, useEffect } from "react";
import { IUserData } from "models/Employee";
import fetchApi from "api/fetchApi";

export interface AuthContextInterface {
  user: IUserData | null;
  loading: boolean;
  login: (data: any) => void;
  logout: () => void;
}

export const AuthContextDefaults: AuthContextInterface = {
  user: null,
  loading: true,
  login: () => {},
  logout: () => {},
};

export const AuthContext = createContext<AuthContextInterface>(AuthContextDefaults);

type Props = {
  children: ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [user, setUser] = useState<IUserData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // eslint-disable-next-line
  const [userTokenExpiresIn, setUserTokenExpiresIn] = useState<any>(null);

  const login = async (e: any) => {
    setLoading(true);
    const userData = JSON.stringify(e.data);
    try {
      localStorage.setItem("userData", userData);
      localStorage.setItem("token", e.data.token);

      setUser(e.data);

    } catch (error) {
      throw new Error("Error while saving user to local storage");
    }
    setTimeout(() => {
      console.log("User logged in");
      setLoading(false);
    }, 1200);
  };

  const logout = () => {
    setLoading(true);
    try {
      localStorage.removeItem("userData");
      localStorage.removeItem("token");
      setUser(null);
    } catch (error) {
      throw new Error("Error while removing user from local storage");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadLocalUser = async () => {
      setLoading(true);
      const storedUserData = localStorage.getItem("userData");

      if (storedUserData) {
        const userData = JSON.parse(storedUserData) as IUserData;
        const { user } = userData;

        if (user) {
          try {
            const userResponse = await fetchApi(`users/${user.id}`).get("");
            if (userResponse.data) {
              setUser(userResponse.data);
              console.log("User loaded from local storage", userResponse.data);
            }
          } catch (error) {
            console.error("Error fetching user data:", error);
            setUser(null);
          }
        }
      }
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    };

    loadLocalUser();
  }, []);

  const value = useMemo(() => {
    return {
      user,
      loading,
      login,
      logout,
    };
    // eslint-disable-next-line
  }, [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
